import React from "react";
import { FIELD_TYPES } from "../../config/fieldDefinitions";
import {
  DateTimeSpinnerInput,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
} from "@exiftrator/shared/components/ui";

const FieldCardRow = React.forwardRef(function FieldCardRow(
  {
    field,
    value,
    hasMultiple,
    isEdited,
    error,
    onChange,
    onCommit,
    onRevert,
  },
  ref,
) {
  const hasError = Boolean(error);
  const containerClassName = [
    "scroll-mt-24",
    isEdited ? "bg-bg-hover/10" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const commit = (explicitValue) => {
    onCommit(field.id, explicitValue);
  };

  const handleChange = (event) => {
    let nextValue = event.target.value;

    if (field.type === FIELD_TYPES.NUMBER || field.type === FIELD_TYPES.GPS_COORDINATE) {
      nextValue = nextValue === "" ? null : parseFloat(nextValue);
    }

    onChange(field.id, nextValue);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onRevert(field.id);
      return;
    }

    if (event.key !== "Enter") return;
    if (field.type === FIELD_TYPES.TEXTAREA && !event.metaKey && !event.ctrlKey) return;

    event.preventDefault();
    commit();
  };

  const placeholder = hasMultiple ? "Mixed values" : undefined;

  const renderInput = () => {
    switch (field.type) {
      case FIELD_TYPES.TEXTAREA:
        return (
          <Textarea
            value={value || ""}
            onChange={handleChange}
            onBlur={() => commit()}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={3}
          />
        );

      case FIELD_TYPES.SELECT:
        return (
          <Select
            value={value?.toString() ?? ""}
            onValueChange={(selectedValue) => {
              const nextValue = selectedValue === "" ? null : parseInt(selectedValue, 10);
              onChange(field.id, nextValue);
              commit(nextValue);
            }}
          >
            <SelectTrigger className="min-h-[44px] w-full">
              <SelectValue placeholder={placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case FIELD_TYPES.NUMBER:
      case FIELD_TYPES.GPS_COORDINATE:
        return (
          <Input
            type="number"
            value={
              field.type === FIELD_TYPES.GPS_COORDINATE && value != null
                ? Math.round(value * 10_000_000) / 10_000_000
                : (value ?? "")
            }
            onChange={handleChange}
            onBlur={() => commit()}
            onKeyDown={handleKeyDown}
            step="any"
            className="min-h-[44px]"
            placeholder={placeholder}
          />
        );

      case FIELD_TYPES.DATE:
        return (
          <DateTimeSpinnerInput
            value={value}
            size="compact"
            shortLabels={false}
            onChange={(nextValue) => onChange(field.id, nextValue)}
            onBlur={(nextValue) => commit(nextValue)}
            onKeyDown={handleKeyDown}
          />
        );

      case FIELD_TYPES.TEXT:
      default:
        return (
          <Input
            type="text"
            value={value || ""}
            onChange={handleChange}
            onBlur={() => commit()}
            onKeyDown={handleKeyDown}
            className="min-h-[44px]"
            placeholder={placeholder}
          />
        );
    }
  };

  return (
    <div id={`field-${field.id}`} data-field-id={field.id} ref={ref} className={containerClassName}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <Label className="normal-case text-xs tracking-wide text-text-secondary">
          {field.label}
        </Label>
        {hasMultiple && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            Mixed
          </span>
        )}
      </div>

      {renderInput()}

      {hasError && (
        <p className="mt-1.5 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

export default FieldCardRow;
